import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, RotateCcw, MapPin, Bus, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin settings — Regras de emissão de estimativa.
 * Substitui a antiga lista hardcoded {"Viana do Castelo", "Braga", "Porto", "Coimbra",
 * "Leiria", "Santarém", "Faro"} por uma lista editável, sem necessidade de deploy.
 */
export default function EstimateRulesSettings() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [newDistrict, setNewDistrict] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/estimate-rules");
      const data = r.data || {};
      setDefaults(data.defaults || []);
      // eslint-disable-next-line no-unused-vars
      const { defaults: _d, ...rest } = data;
      setCfg(rest);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar regras");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/estimate-rules", {
        authorized_districts: cfg.authorized_districts,
        block_when_no_transport: !!cfg.block_when_no_transport,
      });
      setCfg(r.data);
      toast.success("Regras de estimativa gravadas · em vigor imediato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const removeDistrict = (idx) => {
    const next = [...(cfg.authorized_districts || [])];
    next.splice(idx, 1);
    setCfg({ ...cfg, authorized_districts: next });
  };

  const addDistrict = () => {
    const s = (newDistrict || "").trim();
    if (!s) return;
    const list = cfg.authorized_districts || [];
    if (list.some((d) => d.toLowerCase() === s.toLowerCase())) {
      toast.error(`"${s}" já está na lista`);
      return;
    }
    setCfg({ ...cfg, authorized_districts: [...list, s] });
    setNewDistrict("");
  };

  const resetToDefaults = () => {
    setCfg({ ...cfg, authorized_districts: [...defaults] });
    toast.info("Lista reposta para os defaults · lembre-se de gravar");
  };

  if (loading || !cfg) {
    return (
      <div className="p-8 lg:p-12 max-w-4xl mx-auto flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" /> A carregar…
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-8" data-testid="estimate-rules-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configurações</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Regras da Estimativa</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
          Distritos onde o Atendimento pode emitir <b>estimativa directa</b> sem passar pela Reunião Técnica.
          Fora desta lista, o sistema bloqueia a geração/envio de valores e força o agendamento de RT.
          As alterações entram em vigor imediatamente (sem redeploy).
        </p>
      </div>

      {/* Distritos autorizados */}
      <section className="bg-card border border-border rounded-2xl p-6 lg:p-8 space-y-4" data-testid="districts-section">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-foreground" />
          <h2 className="font-ui text-lg">Distritos autorizados</h2>
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-foreground text-xs font-bold" data-testid="districts-count">
            {(cfg.authorized_districts || []).length}
          </span>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="districts-list">
          {(cfg.authorized_districts || []).map((d, i) => (
            <div key={`${d}-${i}`} data-testid={`district-chip-${i}`} className="inline-flex items-center gap-2 bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 text-[hsl(var(--success))] rounded-full px-3 py-1.5 text-sm">
              <span className="font-semibold">{d}</span>
              <button
                type="button"
                onClick={() => removeDistrict(i)}
                data-testid={`district-remove-${i}`}
                className="text-[hsl(var(--success))] hover:text-[hsl(var(--danger))]"
                title={`Remover ${d}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {(cfg.authorized_districts || []).length === 0 && (
            <span className="text-xs text-muted-foreground italic">Nenhum distrito autorizado — a estimativa está bloqueada em <b>todos</b> os leads.</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border/60">
          <input
            type="text"
            value={newDistrict}
            onChange={(e) => setNewDistrict(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDistrict(); } }}
            placeholder="Ex.: Aveiro"
            data-testid="district-input"
            className="flex-1 min-w-[220px] bg-card border border-border rounded-full px-4 py-2 text-sm focus:outline-none focus:border-primary"
          />
          <button
            onClick={addDistrict}
            disabled={!newDistrict.trim()}
            data-testid="district-add"
            className="inline-flex items-center gap-1.5 bg-foreground hover:bg-foreground/90 text-background rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Adicionar
          </button>
          <button
            onClick={resetToDefaults}
            data-testid="district-reset"
            className="inline-flex items-center gap-1.5 bg-muted hover:bg-muted text-foreground border border-border rounded-md px-3 py-1.5 text-xs font-semibold"
            title={`Repor para: ${defaults.join(", ")}`}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Repor defaults
          </button>
        </div>
      </section>

      {/* Regra de transporte público */}
      <section className="bg-card border border-border rounded-2xl p-6 lg:p-8 space-y-4" data-testid="transport-section">
        <div className="flex items-center gap-2 mb-2">
          <Bus className="w-4 h-4 text-foreground" />
          <h2 className="font-ui text-lg">Transportes públicos regulares</h2>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Quando activo, leads em que o Atendimento marcou <b>sem transportes públicos regulares</b> para o local do serviço
          têm a estimativa bloqueada — só é permitido agendar Reunião Técnica.
        </p>
        <label className="inline-flex items-center gap-2.5 text-sm cursor-pointer">
          <input
            type="checkbox"
            data-testid="transport-toggle"
            checked={!!cfg.block_when_no_transport}
            onChange={(e) => setCfg({ ...cfg, block_when_no_transport: e.target.checked })}
            className="w-4 h-4 accent-[#1D4ED8]"
          />
          <span className="font-semibold text-foreground">
            Bloquear estimativa quando não há transportes públicos regulares
          </span>
        </label>
      </section>

      {/* Save */}
      <div className="flex items-center justify-end gap-3 sticky bottom-4 z-10">
        {(cfg.authorized_districts || []).length === 0 && (
          <span className="text-xs text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 rounded-full px-3 py-1.5 font-semibold" data-testid="empty-warning">
            ⚠️ Lista vazia — adicione pelo menos um distrito antes de gravar.
          </span>
        )}
        <button
          onClick={save}
          disabled={saving || (cfg.authorized_districts || []).length === 0}
          data-testid="save-estimate-rules"
          className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar regras
        </button>
      </div>

      {/* Efeito na app */}
      <section className="bg-muted border border-border rounded-2xl p-6 space-y-2" data-testid="preview-effect">
        <div className="flex items-center gap-2 text-foreground">
          <ShieldCheck className="w-4 h-4" />
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold">O que estas regras controlam</div>
        </div>
        <ul className="text-sm text-[#5C4326] list-disc list-inside space-y-1 leading-relaxed">
          <li>Banner de bloqueio na página do lead (<b>Estimativa bloqueada</b>).</li>
          <li>Botões de "Gerar estimativa" / "Enviar ao cliente" ficam desactivados.</li>
          <li>A API devolve <code>403</code> em POST/PATCH/send para leads bloqueados.</li>
          <li>Continua sempre possível <b>agendar Reunião Técnica</b> para o Gestor avaliar in loco.</li>
        </ul>
      </section>
    </div>
  );
}
